import type { DataTableColumn, DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import { InputSearch } from '@/components/custom/erp/data-table-v2/search-inputs';
import { formatDate, formatMoney, formatRoundedNumber } from '@/lib/formatUtils';

export const getDataTableColumns = (
  handleOpenViewForm: (row: MuaHangRecord) => void,
  totalAmount?: number | null,
  isTotalLoading?: boolean
): DataTableColumn[] => [
  {
    field: 'status',
    headerName: 'Trạng thái',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => {
      switch (params.row.status) {
        case '0':
          return 'Chưa ghi sổ';
        case '3':
          return 'Chờ duyệt';
        case '5':
          return 'Đã ghi sổ';
        default:
          return '';
      }
    }
  },
  {
    field: 'so_ct',
    headerName: 'Số c/từ',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <div
        className='cursor-pointer text-main hover:underline'
        onClick={e => {
          e.stopPropagation();
          handleOpenViewForm(params.row);
        }}
      >
        {params.row.so_ct}
      </div>
    )
  },
  {
    field: 'ngay_ct',
    headerName: 'Ngày c/từ',
    width: 120,
    renderCell: params => formatDate(params.row.ngay_ct)
  },
  {
    field: 'ma_kh',
    headerName: 'Mã khách hàng',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_kh_data?.customer_code
  },
  {
    field: 'ten_kh',
    headerName: 'Tên khách hàng',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_kh_data?.customer_name
  },
  { field: 'dien_giai', headerName: 'Diễn giải', width: 200 },
  {
    field: 'tk',
    headerName: 'Tài khoản',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.tk_data?.code
  },
  {
    field: 't_tt_nt',
    headerName: 'Tổng tiền',
    width: 120,
    type: 'number',
    renderCell: params => formatRoundedNumber(params.row.t_tt_nt),
    renderSearchInput: params => (
      <InputSearch
        {...params}
        onKeyDown={params.onKeyDown ?? (() => {})}
        suffixContent={
          <div className='flex items-center gap-2'>
            <span className='text-lg font-bold'>&#931;</span>
            <span className='font-semibold text-main'>
              {isTotalLoading
                ? 'Đang tính...'
                : typeof totalAmount === 'number'
                  ? formatRoundedNumber(totalAmount)
                  : '—'}
            </span>
          </div>
        }
      />
    )
  },
  {
    field: 'ma_nt',
    headerName: 'Ngoại tệ',
    width: 100,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_nt_data?.ma_nt
  }
];

export const getInputTableColumns: DataTableColumn[] = [
  {
    field: 'ma_vt',
    headerName: 'Mã sản phẩm',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_vt_data?.ma_vt
  },
  {
    field: 'ten_vt',
    headerName: 'Tên sản phẩm',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_vt_data?.ten_vt
  },
  {
    field: 'dvt',
    headerName: 'Đvt',
    width: 80,
    renderCell: (params: DataTableRenderCellParams) => params.row.dvt_data?.ten_dvt
  },
  {
    field: 'ma_kho',
    headerName: 'Mã kho',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_kho_data?.ma_kho
  },
  { field: 'ma_vi_tri', headerName: 'Mã vị trí', width: 120 },
  { field: 'so_luong', headerName: 'Số lượng', width: 120, type: 'number' },
  { field: 'tien0', headerName: 'Tiền hàng', width: 120, type: 'number' },
  { field: 'tien3', headerName: 'Tiền tính thuế nk', width: 120, type: 'number' },
  { field: 'cp_nt', headerName: 'Chi phí VND', width: 120, type: 'number' },
  {
    field: 'tk_vt',
    headerName: 'Tk nợ',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.tk_vt_data?.code
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_bp_data?.ma_bp
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_vv_data?.ma_vu_viec
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_hd_data?.ma_hd
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_dtt_data?.ma_dtt
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_ku_data?.ma_ku
  },
  { field: 'ma_phi', headerName: 'Phí', width: 120 },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_sp_data?.ma_vt
  },
  { field: 'ma_lsx', headerName: 'Lệnh sản xuất', width: 120 },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => params.row.ma_cp0_data?.ma_cpkhl
  },
  { field: 'so_ct_hd4', headerName: 'Số hóa đơn mua trong nước', width: 150 },
  { field: 'so_ct_hd5', headerName: 'Số hóa đơn mua nhập khẩu', width: 150 },
  { field: 'so_ct_hd7', headerName: 'Số hóa đơn mua xuất thẳng', width: 150 },
  { field: 'line_hd1', headerName: 'Dòng HĐ', width: 100 }
];

export const historyColumns: DataTableColumn[] = [
  { field: 'username', headerName: 'Tài khoản', width: 100 },
  { field: 'nickname', headerName: 'Tên tài khoản', width: 200 },
  { field: 'action', headerName: 'Hành động', width: 100 },
  { field: 'datetime0', headerName: 'Thời gian', width: 150 }
];
